import {
  type HTMLAttributes,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ModalContext } from '../contexts/ModalContext.js';

/**
 * @public
 */
export interface ModalContextProviderProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

/**
 * The ModalProvider keeps track of how many modals are open in its descendants.
 * The implementation of addModal() here increases
 * the count of modal levels. Elsewhere, the useModal hook calls addModal().
 * Likewise, when the modal is unmounted, removeModal() is called and the count is
 * correspondingly decreased by the implementation here.
 *
 * If the modalCount is greater than zero then `aria-hidden` is added to
 * hide the contents outside the modal from screen readers. We use a React
 * context for the purpose of tracking.
 * @public
 */
export function ModalContextProvider(
  props: Readonly<ModalContextProviderProps>,
) {
  const { children } = props;
  const parent = useContext(ModalContext);
  const [modalCount, setModalCount] = useState(0);
  const contextValue = useMemo(
    () => ({
      parent,
      modalCount,
      addModal() {
        setModalCount((count: number) => count + 1);
        if (parent) {
          parent.addModal();
        }
      },
      removeModal() {
        setModalCount((count: number) => count - 1);
        if (parent) {
          parent.removeModal();
        }
      },
    }),
    [parent, modalCount],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
}
